import { Star, Clock, Phone, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Google Business Profiles for all Kenscho divisions
const GOOGLE_BUSINESS_PROFILES = {
  // Main Kenscho Design Inc
  design: {
    name: { en: 'Kenscho Design Inc', fr: 'Kenscho Design Inc' },
    category: { en: 'General Contractor', fr: 'Entrepreneur général' },
    rating: 5.0,
    reviewCount: 1,
    phone: '+1 514-291-3737',
    hours: { en: 'Mon-Fri: 9 AM – 5 PM', fr: 'Lun-Ven: 9h – 17h' },
    website: 'https://www.kenscho.ca',
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d178784.94895997045!2d-73.87424795!3d45.5591827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91b7c80f5b2ed%3A0xa1ae591e02c24d31!2sKenscho%20Design%20Inc!5e0!3m2!1sen!2sca!4v1707849600000!5m2!1sen!2sca',
    profileUrl: 'https://maps.app.goo.gl/4vBrE4XNiqErabFKA',
    reviewUrl: 'https://search.google.com/local/writereview?placeid=ChIJ_SUkkHwrtYkRMU3CAh5ZrtE'
  },
  // Location Kenscho (Equipment Rental)
  equipment: {
    name: { en: 'Location Kenscho', fr: 'Location Kenscho' },
    category: { en: 'Tool Rental Service', fr: 'Service de location d\'outils' },
    rating: 0,
    reviewCount: 0,
    phone: '+1 514-291-3737',
    hours: { en: 'Mon-Fri: 9 AM – 5 PM', fr: 'Lun-Ven: 9h – 17h' },
    website: 'https://kenscho.ca/equipment-rental',
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d178784.94895997045!2d-73.87424795!3d45.5591827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91bb4d166fbde%3A0xdefb66d1bbaedd19!2sLocation%20Kenscho!5e0!3m2!1sen!2sca!4v1707849600000!5m2!1sen!2sca',
    profileUrl: 'https://share.google/d5hACNlmP5cYbZZ1Q',
    reviewUrl: 'https://search.google.com/local/writereview?placeid=ChIJ3vsWpLSRyUwRGd2tu9Fm-94'
  },
  // Soudure Kenscho (Welding)
  welding: {
    name: { en: 'Soudure Kenscho', fr: 'Soudure Kenscho' },
    category: { en: 'Welder', fr: 'Soudeur' },
    rating: 0,
    reviewCount: 0,
    phone: '+1 514-291-3737',
    hours: { en: 'Mon-Sat: 9 AM – 5 PM', fr: 'Lun-Sam: 9h – 17h' },
    website: 'https://kenscho.ca/welding',
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d178784.94895997045!2d-73.87424795!3d45.5591827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91a4cd97fd2b0%3A0xeb8b7b4bad445527!2sSoudure%20Kenscho!5e0!3m2!1sen!2sca!4v1707849600000!5m2!1sen!2sca',
    profileUrl: 'https://share.google/iSY0gqdIE97Hzuy61',
    reviewUrl: 'https://search.google.com/local/writereview?placeid=ChIJsNJ_2UwaycwRJ1VErUt7i-s'
  },
  // Transport Kenscho
  transport: {
    name: { en: 'Transport Kenscho', fr: 'Transport Kenscho' },
    category: { en: 'Transportation Service', fr: 'Service de transport' },
    rating: 0,
    reviewCount: 0,
    phone: '+1 514-291-3737',
    hours: { en: 'Open 24 hours', fr: 'Ouvert 24h' },
    website: 'https://kenscho.ca/transport',
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d178784.94895997045!2d-73.87424795!3d45.5591827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91a037ce3efd0%3A0xad7f4338226d83bc!2sTransport%20Kenscho!5e0!3m2!1sen!2sca!4v1707849600000!5m2!1sen!2sca',
    profileUrl: 'https://maps.app.goo.gl/ftnKmDcSDkUArDum7',
    reviewUrl: 'https://search.google.com/local/writereview?placeid=ChIJ0O_jfAMayUwRvINtIjhDf60'
  }
};

// Star rating component
const StarRating = ({ rating, showEmpty = false }) => {
  if (rating === 0 && !showEmpty) {
    return null;
  }
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating 
              ? 'fill-yellow-400 text-yellow-400' 
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
};

// Google Business Badge Component - Compact version for footer
export const GoogleBusinessBadge = ({ profile = 'design', className = '' }) => {
  const { language } = useLanguage();
  const data = GOOGLE_BUSINESS_PROFILES[profile] || GOOGLE_BUSINESS_PROFILES.design;
  
  return (
    <a 
      href={data.profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`block bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-colors ${className}`}
      data-testid="google-business-badge"
    >
      <div className="flex items-center gap-3">
        {/* Google G Logo */}
        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" className="w-6 h-6">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </div>
        
        <div className="flex-1 min-w-0">
          {data.rating > 0 ? (
            <div className="flex items-center gap-2 mb-1">
              <StarRating rating={data.rating} />
              <span className="text-white font-medium">{data.rating}</span>
            </div>
          ) : (
            <p className="text-white font-medium mb-1">{data.name[language]}</p>
          )}
          <p className="text-white/70 text-sm">
            {language === 'fr' ? 'Voir sur Google' : 'View on Google'}
          </p>
        </div>
        
        <ExternalLink className="h-4 w-4 text-white/50" />
      </div>
    </a>
  );
};

// Full Google Business Section Component
export const GoogleBusinessSection = ({ profile = 'design', showMap = true, className = '' }) => {
  const { language } = useLanguage();
  const data = GOOGLE_BUSINESS_PROFILES[profile] || GOOGLE_BUSINESS_PROFILES.design;
  
  return (
    <div className={`bg-muted/50 border border-border/40 rounded-2xl overflow-hidden ${className}`} data-testid="google-business-section">
      {/* Map */}
      {showMap && (
        <div className="aspect-[16/9] md:aspect-[21/9]">
          <iframe
            src={data.mapEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={data.name[language]}
          />
        </div>
      )}
      
      {/* Business Info */}
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          {/* Left: Business details */}
          <div className="flex items-start gap-4">
            {/* Google G Logo */}
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <svg viewBox="0 0 24 24" className="w-8 h-8">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-1">{data.name[language]}</h3>
              <p className="text-muted-foreground text-sm mb-2">
                {data.category[language]}
              </p>
              
              {/* Rating - only show if there are reviews */}
              {data.rating > 0 ? (
                <div className="flex items-center gap-2 mb-3">
                  <StarRating rating={data.rating} />
                  <span className="font-medium">{data.rating}</span>
                  <span className="text-muted-foreground text-sm">
                    ({data.reviewCount} {language === 'fr' ? 'avis' : data.reviewCount === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'fr' ? 'Soyez le premier à laisser un avis!' : 'Be the first to leave a review!'}
                </p>
              )}
              
              {/* Hours & Phone */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  <span>{data.hours[language]}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  <span>{data.phone}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right: Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={data.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors"
              data-testid="google-profile-btn"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {language === 'fr' ? 'Voir le profil' : 'View Profile'}
            </a>
            <a
              href={data.reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
              data-testid="google-review-btn"
            >
              <Star className="h-4 w-4" />
              {language === 'fr' ? 'Laisser un avis' : 'Leave a Review'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact footer version with just badge and review button
export const GoogleBusinessFooter = ({ profile = 'design', className = '' }) => {
  const { language } = useLanguage();
  const data = GOOGLE_BUSINESS_PROFILES[profile] || GOOGLE_BUSINESS_PROFILES.design;
  
  return (
    <div className={`${className}`}>
      <h4 className="font-semibold text-lg mb-4">
        {language === 'fr' ? 'Suivez-nous sur Google' : 'Find Us on Google'}
      </h4>
      
      <div className="space-y-3">
        {/* Rating Badge */}
        <a 
          href={data.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 group"
          data-testid="footer-google-rating"
        >
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" className="w-6 h-6">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          <div>
            {data.rating > 0 ? (
              <div className="flex items-center gap-2">
                <StarRating rating={data.rating} />
                <span className="text-white font-medium">{data.rating}</span>
              </div>
            ) : (
              <p className="text-white font-medium">{data.name[language]}</p>
            )}
            <p className="text-white/60 text-sm group-hover:text-white/80 transition-colors">
              {language === 'fr' ? 'Voir notre profil Google' : 'View our Google profile'}
            </p>
          </div>
        </a>
        
        {/* Review Link */}
        <a
          href={data.reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium transition-colors"
          data-testid="footer-review-link"
        >
          <Star className="h-4 w-4" />
          {language === 'fr' ? 'Laisser un avis' : 'Leave a review'}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
};
